import {
  ApiOutlined,
  BankOutlined,
  BarChartOutlined,
  DashboardOutlined,
  DollarOutlined,
  GlobalOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  ShoppingOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Layout as AntLayout, Button, Dropdown, Menu, theme } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { useConstantsStore } from '@/stores/constants'
import { useSettingsStore } from '@/stores/settings'

const { Sider, Content, Header } = AntLayout

// Menu items will be generated with translations in the component

interface LayoutProps {
  children: React.ReactNode
}

export function Layout({ children }: LayoutProps) {
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = theme.useToken()
  const { t } = useTranslation()
  const fetchConstants = useConstantsStore((state) => state.fetchConstants)
  const { getPlatformName, getLogoUrl, getPrimaryColor, getSupportUrl, getSupportEmail } =
    useSettingsStore()
  const { user, clearAuth } = useAuthStore()

  // Menu items with translations
  const menuItems = [
    { key: '/', icon: <DashboardOutlined />, label: t('menu.dashboard') },
    { key: '/supplies', icon: <GlobalOutlined />, label: t('menu.supplies') },
    { key: '/demands', icon: <ApiOutlined />, label: t('menu.demands') },
    { key: '/deals', icon: <ShoppingOutlined />, label: t('menu.deals') },
    { key: '/floors', icon: <DollarOutlined />, label: t('menu.floors') },
    { key: '/finance', icon: <BankOutlined />, label: t('menu.finance') },
    { key: '/reports', icon: <BarChartOutlined />, label: t('menu.reports') },
    { key: '/settings', icon: <SettingOutlined />, label: t('menu.settings') },
  ]

  // Fetch constants on mount (settings are fetched in App.tsx)
  useEffect(() => {
    fetchConstants()
  }, [fetchConstants])

  const platformName = getPlatformName()
  const logoUrl = getLogoUrl()
  const primaryColor = getPrimaryColor()
  const supportUrl = getSupportUrl()
  const supportEmail = getSupportEmail()

  const getSelectedKey = () => {
    const path = location.pathname
    const match = menuItems.find((item) => item.key !== '/' && path.startsWith(item.key))
    return match ? match.key : '/'
  }

  const getPageTitle = () => {
    const path = location.pathname
    const match = menuItems.find((item) => item.key !== '/' && path.startsWith(item.key))
    return match?.label || 'Dashboard'
  }

  const siderWidth = collapsed ? 80 : 200

  // Get first letter for collapsed state when no logo
  const getFirstLetter = () => {
    return platformName.charAt(0).toUpperCase()
  }

  const handleLogout = () => {
    clearAuth()
    navigate('/login', { replace: true })
  }

  const handleSupport = () => {
    if (supportUrl) {
      window.open(supportUrl, '_blank')
    } else if (supportEmail) {
      window.location.href = `mailto:${supportEmail}`
    }
  }

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('common.profile'),
      onClick: () => navigate('/profile'),
    },
    ...(supportUrl || supportEmail
      ? [
          {
            key: 'support',
            icon: <QuestionCircleOutlined />,
            label: 'Support',
            onClick: handleSupport,
          },
        ]
      : []),
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('common.logout'),
      onClick: handleLogout,
    },
  ]

  return (
    <AntLayout className="min-h-screen">
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={200}
        collapsedWidth={80}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          height: '100vh',
          overflow: 'auto',
          zIndex: 100,
          background: primaryColor,
        }}
      >
        <button
          type="button"
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: collapsed ? '0 8px' : '0 16px',
            background: 'none',
            border: 'none',
            width: '100%',
          }}
          onClick={() => navigate('/')}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={platformName}
              style={{
                maxHeight: 40,
                maxWidth: collapsed ? 48 : 160,
                objectFit: 'contain',
              }}
            />
          ) : collapsed ? (
            <span
              style={{
                fontSize: 24,
                fontWeight: 700,
                color: '#fff',
              }}
            >
              {getFirstLetter()}
            </span>
          ) : (
            <span
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#fff',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {platformName}
            </span>
          )}
        </button>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{
            borderRight: 0,
            background: 'transparent',
          }}
        />
      </Sider>

      <AntLayout style={{ marginLeft: siderWidth, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            padding: '0 24px',
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 16, width: 40, height: 40 }}
            />
            <span style={{ fontSize: 16, fontWeight: 500 }}>{getPageTitle()}</span>
          </div>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Button type="text" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserOutlined />
              <span style={{ color: token.colorTextSecondary }}>{user?.email || 'User'}</span>
            </Button>
          </Dropdown>
        </Header>

        <Content style={{ margin: 24 }}>{children}</Content>
      </AntLayout>
    </AntLayout>
  )
}
